import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, ShoppingCart, ExternalLink, Loader2, Sparkles, DollarSign,
  Star, Wifi, HardDrive, Shield, Monitor, Server, Cable, Laptop, Printer,
  Tag, TrendingUp, Lightbulb
} from 'lucide-react';
import { LeadCapture } from '../components/LeadCapture';

const CATEGORY_ICONS = {
  networking: Wifi, storage: HardDrive, security: Shield, peripherals: Monitor,
  servers: Server, cables: Cable, laptops: Laptop, printers: Printer,
};

export default function TechFinder() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Tech Finder');
  const [query, setQuery] = useState('');
  const [budget, setBudget] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/api/techfinder/popular');
      setCategories(data.categories || []);
    } catch (e) { /* handled */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const searchProducts = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q) { toast.error('Enter what you need'); return; }
    setSearching(true);
    try {
      const { data } = await api.post('/api/techfinder/search', { query: q, budget });
      setResults(data);
    } catch { toast.error('Search failed'); } finally { setSearching(false); }
  };

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="tech-finder">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#D4A843]" /> Tech Finder</h1>
            <p className="text-sm text-gray-500 mt-1">AI-powered product recommendations for your technology needs</p>
          </div>

          {/* Search Bar */}
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="tech-search">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-gray-600 font-medium">What do you need?</label>
                <Input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchProducts()} className="mt-1 rounded-lg text-base h-12" placeholder="e.g. Best router for a small office, 16GB RAM for Dell laptop, security camera system..." data-testid="tech-search-input" />
              </div>
              <div className="w-32">
                <label className="text-xs text-gray-600 font-medium">Budget</label>
                <Input value={budget} onChange={e => setBudget(e.target.value)} className="mt-1 rounded-lg h-12" placeholder="$100" data-testid="tech-budget" />
              </div>
              <Button onClick={() => searchProducts()} disabled={searching} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg h-12 px-6" data-testid="tech-search-btn">
                {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Find Products
              </Button>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="space-y-4 animate-fade-in" data-testid="tech-results">
              {/* Pro Tip */}
              {results.pro_tip && (
                <div className="bg-[#D4A843]/10 border border-[#D4A843]/30 rounded-lg p-4 flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-[#D4A843] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-[#D4A843]">Expert Tip</p>
                    <p className="text-sm text-gray-700 mt-0.5">{results.pro_tip}</p>
                  </div>
                </div>
              )}

              {/* Product Cards */}
              {results.recommendations?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.recommendations.map((rec, i) => (
                    <div key={`item-${i}`} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift" data-testid={`tech-product-${i}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm font-bold text-gray-900 flex-1 line-clamp-2">{rec.name}</h3>
                        {rec.rating && <span className="flex items-center gap-0.5 text-[10px] text-amber-600 shrink-0 ml-2"><Star className="w-3 h-3 fill-amber-500 text-amber-500" />{rec.rating}</span>}
                      </div>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-3">{rec.description}</p>
                      {rec.best_for && <p className="text-[10px] text-[#D4A843] bg-blue-50 rounded px-2 py-1 mb-3">Best for: {rec.best_for}</p>}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <span className="text-lg font-bold text-emerald-600">{rec.estimated_price}</span>
                        <a href={rec.amazon_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF9900] hover:bg-[#e8890a] text-white text-xs font-bold rounded-lg transition-colors" data-testid={`buy-btn-${i}`}>
                          <ShoppingCart className="w-3.5 h-3.5" /> Buy on Amazon
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No specific products found. Try a broader search.</p>
                </div>
              )}

              {/* Browse All on Amazon */}
              <div className="text-center">
                <a href={results.amazon_search_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors" data-testid="browse-amazon">
                  <ExternalLink className="w-4 h-4" /> Browse All Results on Amazon
                </a>
                <p className="text-[10px] text-gray-400 mt-2">Powered by Amazon Associates | {results.affiliate_tag}</p>
              </div>
            </div>
          )}

          {/* Browse by Category */}
          {!results && categories.length > 0 && (
            <div data-testid="tech-categories">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Browse by Category</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {categories.map(cat => {
                  const Icon = CATEGORY_ICONS[cat.id] || ShoppingCart;
                  return (
                    <button key={cat.id} onClick={() => { setQuery(cat.keywords); searchProducts(cat.keywords); }} className="bg-white border border-gray-200 rounded-lg p-4 text-left hover-lift hover:border-[#D4A843]/30 transition-all group" data-testid={`cat-${cat.id}`}>
                      <Icon className="w-6 h-6 text-[#D4A843] mb-2 group-hover:text-[#D4A843] transition-colors" />
                      <h3 className="text-sm font-bold text-gray-900">{cat.name}</h3>
                      <p className="text-[10px] text-gray-500 mt-0.5">{cat.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* How It Works */}
          {!results && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">How Tech Finder Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3">
                  <div className="w-10 h-10 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-2"><Search className="w-5 h-5 text-[#D4A843]" /></div>
                  <p className="text-xs font-bold text-gray-900">1. Describe Your Need</p>
                  <p className="text-[10px] text-gray-500 mt-1">Tell us what tech problem you're solving or what equipment you need</p>
                </div>
                <div className="text-center p-3">
                  <div className="w-10 h-10 bg-[#D4A843]/10 rounded-full flex items-center justify-center mx-auto mb-2"><Sparkles className="w-5 h-5 text-[#D4A843]" /></div>
                  <p className="text-xs font-bold text-gray-900">2. AI Recommends</p>
                  <p className="text-[10px] text-gray-500 mt-1">Our AI finds the best products based on your specific needs and budget</p>
                </div>
                <div className="text-center p-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2"><ShoppingCart className="w-5 h-5 text-emerald-600" /></div>
                  <p className="text-xs font-bold text-gray-900">3. Buy with Confidence</p>
                  <p className="text-[10px] text-gray-500 mt-1">Purchase directly from Amazon with expert-vetted recommendations</p>
                </div>
              </div>
            </div>
          )}

          {/* Lead Capture */}
          <LeadCapture magnet="tech_recommendations" page="techfinder" title="Get Personalized Tech Recommendations" subtitle="Enter your email — we'll send you a curated product list based on your search history." variant="inline" />
        </div>
      </main>
    </div>
  );
}
